import type { PropsWithChildren, ReactElement } from 'react';
import { Children, Fragment } from 'react';

import type { SkeletonProps } from '@mantine/core';
import { Group, Skeleton, Text, useMantineTheme } from '@mantine/core';

import { IconChevronsRight } from '@tabler/icons-react';
import { Link, matchPath, useLocation } from 'react-router-dom';

type BreadcrumbsProps = PropsWithChildren;

export function Breadcrumbs({ children }: BreadcrumbsProps) {
  const childrenArray = Children.toArray(children) as ReactElement[];
  const theme = useMantineTheme();
  return (
    <Group noWrap spacing={'02'}>
      {childrenArray.map(item => (
        <Fragment key={item.key}>
          <IconChevronsRight
            size={16}
            style={{ color: theme.colors.gray[5] }}
          />
          {item}
        </Fragment>
      ))}
    </Group>
  );
}

interface BreadcrumbItemProps {
  label: string;
  path: string;
}

function BreadcrumbItem({ label, path }: BreadcrumbItemProps) {
  const { pathname } = useLocation();

  const isActive = (itemPath: string) => !!matchPath(itemPath, pathname);

  return (
    <Link to={path} style={{ textDecoration: 'none' }}>
      <Group noWrap spacing={'01'}>
        <Text size={'xs'} color="black" fw={isActive(path) ? 500 : 400}>
          {label}
        </Text>
      </Group>
    </Link>
  );
}

function BreadcrumbsLoading({ children }: PropsWithChildren) {
  const childrenArray = Children.toArray(children) as ReactElement[];

  return (
    <Group noWrap spacing={'02'}>
      {childrenArray.map(item => (
        <Fragment key={item.key}>
          <Skeleton h={19} w={19} />
          {item}
        </Fragment>
      ))}
    </Group>
  );
}

function BreadcrumbItemLoading(props: SkeletonProps) {
  return <Skeleton h={19} w={100} {...props} />;
}

Breadcrumbs.Item = BreadcrumbItem;
Breadcrumbs.Loading = BreadcrumbsLoading;
Breadcrumbs.ItemLoading = BreadcrumbItemLoading;
